import React, { useState } from "react";
import { Grid, Box, Typography, IconButton, LinearProgress, Button } from "@mui/material";
import { styled } from "@mui/system";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import { MAX_FILE_SIZE } from "config/constants";

const Container = styled(Box)(({ theme }) => ({
  backgroundColor: "#fcfcfc",
  borderRadius: "20px",
  padding: theme.spacing(4),
  textAlign: "center",
}));

const UploadBox = styled(Box)(({ theme }) => ({
  border: "2px dashed",
  borderColor: "#7DD3FC",
  borderRadius: "20px",
  padding: theme.spacing(4),
  marginTop: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
}));

const FileInput = styled("input")({
  display: "none",
});

const DocumentsUpload = ({ handleDocumentUpload }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadQueue, setUploadQueue] = useState([]);

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    const documentFiles = files.filter(
      (file) =>
        [
          "application/pdf", // PDF
          "application/msword", // Microsoft Word (.doc)
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // Microsoft Word (.docx)
          "application/vnd.ms-excel", // Microsoft Excel (.xls)
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // Microsoft Excel (.xlsx)
          "application/vnd.ms-powerpoint", // Microsoft PowerPoint (.ppt)
          "application/vnd.openxmlformats-officedocument.presentationml.presentation", // Microsoft PowerPoint (.pptx)
          "text/plain", // Plain text (.txt)
          "application/rtf", // Rich Text Format (.rtf)
          "application/vnd.oasis.opendocument.text", // OpenDocument Text (.odt)
          "application/vnd.oasis.opendocument.spreadsheet", // OpenDocument Spreadsheet (.ods)
          "application/vnd.oasis.opendocument.presentation", // OpenDocument Presentation (.odp)
          "application/x-iwork-pages-sffpages", // Apple Pages
          "application/x-iwork-keynote-sffkey", // Apple Keynote
          "application/x-iwork-numbers-sffnumbers", // Apple Numbers
        ].includes(file.type) && file.size <= MAX_FILE_SIZE
    );
    setSelectedFiles((prevFiles) => {
      const files = [
        ...prevFiles,
        ...documentFiles.map((file) => ({ blob: URL.createObjectURL(file), file })),
      ];

      handleDocumentUpload(files);
      return files;
    });
  };

  const handleRemoveFile = (fileToRemove) => {
    setSelectedFiles((prevFiles) => {
      const files = prevFiles.filter((item) => item.file !== fileToRemove.file);
      handleDocumentUpload(files);
      return files;
    });
  };

  return (
    <Grid item xs={12} md={12} lg={12}>
      <Container>
        <Typography variant="h6" component="div" gutterBottom>
          Upload Document
        </Typography>
        <Typography variant="body2" color="textSecondary">
          All Valid Document Types (docx, doc, txt etc)
        </Typography>
        <label htmlFor="document-upload">
          <UploadBox>
            <CloudUploadIcon color="info" fontSize="large" />
            <Typography variant="body1" color="textPrimary" sx={{ mt: 2 }}>
              Browse to choose the file here
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Maximum file size should not be more than <strong>20MB</strong>
            </Typography>
          </UploadBox>
        </label>
        <FileInput id="document-upload" type="file" multiple onChange={handleFileChange} />
        {selectedFiles.length > 0 && (
          <Box sx={{ mt: 2 }}>
            {selectedFiles.map((item, index) => (
              <Box
                key={index}
                sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", mt: 1 }}
              >
                <Typography variant="body2" color={'blue'}>{item.file.name}</Typography>
                <IconButton
                  size="small"
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                  }}
                  onClick={() => handleRemoveFile(item)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
        {uploadProgress > 0 && (
          <LinearProgress
            variant="determinate"
            color="info"
            value={uploadProgress}
            sx={{ mt: 2 }}
          />
        )}
        {uploadProgress === 100 && (
          <Typography variant="body1" color="textPrimary" sx={{ mt: 2 }}>
            Upload complete!
          </Typography>
        )}
        {/* <Button variant="contained" color="primary" onClick={handleUpload} sx={{ mt: 2 }}>
          Upload
        </Button> */}
      </Container>
    </Grid>
  );
};

export default DocumentsUpload;
